"use client";

import * as React from "react";
import {
	createContext,
	CSSProperties,
	ReactNode,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import {
	defaultDropAnimationSideEffects,
	DndContext,
	DragCancelEvent,
	DragEndEvent,
	DragOverEvent,
	DragOverlay,
	DragStartEvent,
	DropAnimation,
	KeyboardSensor,
	MeasuringStrategy,
	Modifiers,
	MouseSensor,
	pointerWithin,
	rectIntersection,
	TouchSensor,
	UniqueIdentifier,
	useSensor,
	useSensors,
	type CollisionDetection,
	type DraggableAttributes,
	type DraggableSyntheticListeners,
} from "@dnd-kit/core";
import {
	arrayMove,
	defaultAnimateLayoutChanges,
	rectSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
	type AnimateLayoutChanges,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Loader2Icon, PlusIcon } from "lucide-react";
import { createPortal } from "react-dom";

import { cn } from "../../lib/utils";
import { Skeleton } from "./skeleton";

interface KanbanContextProps<T> {
	columns: Record<string, T[]>;
	setColumns: (columns: Record<string, T[]>) => void;
	getItemId: (item: T) => string;
	columnIds: string[];
	activeId: UniqueIdentifier | null;
	setActiveId: (id: UniqueIdentifier | null) => void;
	findContainer: (id: UniqueIdentifier) => string | undefined;
	isColumn: (id: UniqueIdentifier) => boolean;
	modifiers?: Modifiers;
}

const KanbanContext = createContext<KanbanContextProps<unknown>>({
	columns: {},
	setColumns: () => {},
	getItemId: () => "",
	columnIds: [],
	activeId: null,
	setActiveId: () => {},
	findContainer: () => undefined,
	isColumn: () => false,
	modifiers: undefined,
});

type ColumnContextValue = {
	/** Column id from the parent {@link KanbanColumn} `value`. */
	id: string;
	attributes: DraggableAttributes;
	listeners: DraggableSyntheticListeners | undefined;
	isDragging?: boolean;
	disabled?: boolean;
	isLoading?: boolean;
	skeletonCount?: number;
};

const ColumnContext = createContext<ColumnContextValue | null>(null);

function useColumnContext(component: string): ColumnContextValue {
	const context = useContext(ColumnContext);
	if (!context) {
		throw new Error(`${component} must be used within <KanbanColumn>`);
	}
	return context;
}

const ItemContext = createContext<{
	listeners: DraggableSyntheticListeners | undefined;
	isDragging?: boolean;
	disabled?: boolean;
}>({
	listeners: undefined,
	isDragging: false,
	disabled: false,
});

const IsOverlayContext = createContext(false);

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
	defaultAnimateLayoutChanges({ ...args, wasDragging: true });

const dropAnimationConfig: DropAnimation = {
	sideEffects: defaultDropAnimationSideEffects({
		styles: {
			active: {
				opacity: "0.4",
			},
		},
	}),
};

/**
 * Client-mount gate for the `createPortal` call in KanbanOverlay, which needs
 * `document.body` and so must not run on the server or during hydration.
 *
 * A never-notifying subscription makes `useSyncExternalStore` return the server
 * snapshot (`false`) while rendering on the server and while hydrating, then the
 * client snapshot (`true`) once mounted - the same gate a
 * `useLayoutEffect(() => setMounted(true), [])` provides, minus the extra render
 * pass that `react-hooks/set-state-in-effect` flags. All three functions are
 * module-scoped so their identities stay stable; an inline `getSnapshot` is the
 * classic cause of an infinite re-subscribe loop.
 */
const subscribeToNothing = () => () => {};
const getIsMounted = () => true;
const getIsMountedOnServer = () => false;

// Hoisted so `useSensor`/`DndContext` see stable option identities across renders.
const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 10 } };
const TOUCH_SENSOR_OPTIONS = {
	activationConstraint: { delay: 250, tolerance: 5 },
};
const KEYBOARD_SENSOR_OPTIONS = {
	coordinateGetter: sortableKeyboardCoordinates,
};
const MEASURING_CONFIG = {
	droppable: { strategy: MeasuringStrategy.WhileDragging },
};

/** Where a drag started and where it is trying to land. */
export interface KanbanDropContext<T = unknown> {
	item: T;
	/** Column id the item was dragged from. */
	from: string;
	/** Column id being hovered, or released over. */
	to: string;
	/** Index within `to` the item would land at. */
	overIndex: number;
}

export type KanbanDropRejectedContext<T = unknown> = Omit<
	KanbanDropContext<T>,
	"overIndex"
>;

export interface KanbanMoveEvent<T = unknown> {
	event: DragEndEvent;
	/** The item that moved. */
	item: T;
	/** Column id the item was dragged from. */
	from: string;
	/** Column id the item landed in. */
	to: string;
	/** Final index of the item within `to`. */
	index: number;
	didChangeColumn: boolean;
	/** Put the board back to how it looked before the drag started. */
	restore: () => void;
}

export interface KanbanRootProps<T>
	extends Omit<
		useRender.ComponentProps<"div">,
		"children" | "onDragStart" | "onDragEnd"
	> {
	value: Record<string, T[]>;
	onValueChange: (value: Record<string, T[]>) => void;
	getItemId: (item: T) => string;
	children: ReactNode;
	/**
	 * Called after a drop has been applied to the board. Use it to persist the
	 * change, and call `restore()` if persisting fails.
	 */
	onMove?: (event: KanbanMoveEvent<T>) => void;
	/**
	 * Gate for cross-column drops. Returning `false` stops the item previewing
	 * into that column at all, so there is never an illegal state to undo.
	 * Same-column reordering is never gated.
	 */
	canDrop?: (context: KanbanDropContext<T>) => boolean;
	/** Called when an item is released over a column that `canDrop` refused. */
	onDropRejected?: (context: KanbanDropRejectedContext<T>) => void;
	onDragStart?: (event: DragStartEvent) => void;
	onDragEnd?: (event: DragEndEvent) => void;
	onDragCancel?: (event: DragCancelEvent) => void;
	accessibility?: React.ComponentProps<typeof DndContext>["accessibility"];
	modifiers?: Modifiers;
}

type KanbanDragSource<T> = {
	/** Column the item sat in when the drag started, before any preview. */
	container: string;
	/** Board snapshot from before any drag-over preview touched it. */
	columns: Record<string, T[]>;
};

/** Copy the board with `itemId` lifted out of whichever column holds it. */
function liftItem<T>(
	columns: Record<string, T[]>,
	columnIds: string[],
	getItemId: (item: T) => string,
	itemId: string,
): { columns: Record<string, T[]>; item: T | undefined } {
	const next: Record<string, T[]> = {};
	let lifted: T | undefined;

	for (const columnId of columnIds) {
		const remaining: T[] = [];
		for (const item of columns[columnId] ?? []) {
			if (getItemId(item) === itemId) {
				lifted = item;
				continue;
			}
			remaining.push(item);
		}
		next[columnId] = remaining;
	}

	return { columns: next, item: lifted };
}

/** The root component that provides the kanban context and manages the items state. */
function Kanban<T>({
	value,
	onValueChange,
	getItemId,
	children,
	className,
	render,
	onMove,
	canDrop,
	onDropRejected,
	onDragStart,
	onDragEnd,
	onDragCancel,
	accessibility,
	modifiers,
	...props
}: KanbanRootProps<T>) {
	const columns = value;
	const setColumns = onValueChange;
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
	const dragSourceRef = useRef<KanbanDragSource<T> | null>(null);
	/** Pointer offset the last cross-column preview was applied at. */
	const lastPreviewDeltaRef = useRef<{ x: number; y: number } | null>(null);

	const sensors = useSensors(
		useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
		useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS),
		useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
	);

	const columnIds = useMemo(() => {
		const keys = Object.keys(columns);

		if (process.env.NODE_ENV !== "production") {
			const seen = new Set<string>();
			for (const key of keys) {
				for (const item of columns[key] ?? []) {
					const itemId = getItemId(item);
					if (seen.has(itemId)) {
						// eslint-disable-next-line no-console -- no logger context in packages/ui
						console.warn(
							`[Kanban] Duplicate item id "${itemId}". Item ids must be unique across all columns, or drag and drop will misbehave.`,
						);
						break;
					}
					seen.add(itemId);
				}
			}
		}

		return keys;
	}, [columns, getItemId]);

	const isColumn = useCallback(
		(id: UniqueIdentifier) => columnIds.includes(id as string),
		[columnIds],
	);

	const findContainer = useCallback(
		(id: UniqueIdentifier) => {
			if (isColumn(id)) {
				return id as string;
			}
			return columnIds.find((key) =>
				columns[key].some((item) => getItemId(item) === id),
			);
		},
		[columns, columnIds, getItemId, isColumn],
	);

	// `rectIntersection` scores by intersection over union, which a stretched empty
	// column loses to any grazed card, so item drags go by pointer instead.
	const collisionDetection = useCallback<CollisionDetection>(
		(args) => {
			// Resolving a column drag to a card leaves `over` somewhere
			// `handleDragEnd` refuses to reorder.
			if (isColumn(args.active.id)) {
				return rectIntersection({
					...args,
					droppableContainers: args.droppableContainers.filter((container) =>
						isColumn(container.id),
					),
				});
			}

			const pointerCollisions = pointerWithin(args);
			if (pointerCollisions.length > 0) {
				return pointerCollisions;
			}

			// Keyboard dragging has no pointer, so rects are all there is.
			if (!args.pointerCoordinates) {
				return rectIntersection(args);
			}

			// Pointer is in the gutter between columns. Reporting nothing holds the
			// last preview still; scoring rects here made a paused drag oscillate.
			return [];
		},
		[isColumn],
	);

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			setActiveId(event.active.id);
			lastPreviewDeltaRef.current = null;

			const container = isColumn(event.active.id)
				? undefined
				: findContainer(event.active.id);

			dragSourceRef.current = container ? { container, columns } : null;

			onDragStart?.(event);
		},
		[columns, findContainer, isColumn, onDragStart],
	);

	const handleDragOver = useCallback(
		(event: DragOverEvent) => {
			const { active, over } = event;
			if (!over) {
				return;
			}

			if (isColumn(active.id)) {
				return;
			}

			const activeContainer = findContainer(active.id);
			const overContainer = findContainer(over.id);

			if (!activeContainer || !overContainer) {
				return;
			}

			// Within a column, SortableContext already opens the gap with transforms.
			// Reordering state here would move the cards out from under the pointer on
			// every measuring pass, so the drop is only settled on drag end.
			if (activeContainer === overContainer) {
				return;
			}

			const activeItems = columns[activeContainer];
			const overItems = columns[overContainer];

			const activeIndex = activeItems.findIndex(
				(item: T) => getItemId(item) === active.id,
			);
			if (activeIndex === -1) {
				return;
			}

			// Released over the column itself rather than a card, so append.
			const hoveredIndex = isColumn(over.id)
				? -1
				: overItems.findIndex((item: T) => getItemId(item) === over.id);
			const overIndex = hoveredIndex === -1 ? overItems.length : hoveredIndex;

			const source = dragSourceRef.current;
			const from = source?.container ?? activeContainer;

			// Refuse the preview outright rather than letting the card land somewhere
			// it cannot stay and asking the consumer to undo it afterwards.
			if (
				canDrop &&
				from !== overContainer &&
				!canDrop({
					item: activeItems[activeIndex],
					from,
					to: overContainer,
					overIndex,
				})
			) {
				return;
			}

			// A preview re-parents the card, re-measuring the board and feeding another
			// drag-over back in without the pointer moving - the recursion behind
			// "Maximum update depth exceeded" on a drag held between two columns.
			// Requiring real movement breaks it without costing the drag any latency.
			const { delta } = event;
			const lastDelta = lastPreviewDeltaRef.current;
			if (lastDelta && lastDelta.x === delta.x && lastDelta.y === delta.y) {
				return;
			}
			lastPreviewDeltaRef.current = { x: delta.x, y: delta.y };

			const newActiveItems = [...activeItems];
			const newOverItems = [...overItems];
			const [movedItem] = newActiveItems.splice(activeIndex, 1);
			newOverItems.splice(overIndex, 0, movedItem);

			setColumns({
				...columns,
				[activeContainer]: newActiveItems,
				[overContainer]: newOverItems,
			});
		},
		[canDrop, findContainer, getItemId, isColumn, setColumns, columns],
	);

	const handleDragCancel = useCallback(
		(event: DragCancelEvent) => {
			const source = dragSourceRef.current;
			dragSourceRef.current = null;
			setActiveId(null);

			if (source) {
				setColumns(source.columns);
			}

			onDragCancel?.(event);
		},
		[onDragCancel, setColumns],
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			const source = dragSourceRef.current;
			dragSourceRef.current = null;
			setActiveId(null);
			onDragEnd?.(event);

			const restore = () => {
				if (source) {
					setColumns(source.columns);
				}
			};

			if (!over) {
				// Released outside the board, so undo the drag-over preview.
				restore();
				return;
			}

			// Handle column reordering
			if (isColumn(active.id)) {
				if (isColumn(over.id)) {
					const activeIndex = columnIds.indexOf(active.id as string);
					const overIndex = columnIds.indexOf(over.id as string);
					if (activeIndex !== overIndex) {
						const newOrder = arrayMove(columnIds, activeIndex, overIndex);
						const newColumns: Record<string, T[]> = {};
						newOrder.forEach((key) => {
							newColumns[key] = columns[key];
						});
						setColumns(newColumns);
					}
				}
				return;
			}

			// `columns` may already reflect a cross-column preview, so the current
			// container is where the card sits now and `from` is where it started.
			const currentContainer = findContainer(active.id);
			const overContainer = findContainer(over.id);

			if (!currentContainer || !overContainer) {
				return;
			}

			const from = source?.container ?? currentContainer;
			const itemId = String(active.id);

			let nextColumns: Record<string, T[]>;
			let movedItem: T | undefined;
			let index: number;

			if (currentContainer === overContainer) {
				// Pure reorder. `arrayMove` keeps dnd-kit's "take that slot" semantics,
				// which a lift-and-insert would get wrong when dragging downwards.
				const list = columns[currentContainer];
				const activeIndex = list.findIndex(
					(item: T) => getItemId(item) === itemId,
				);
				const overIndex = isColumn(over.id)
					? list.length - 1
					: list.findIndex((item: T) => getItemId(item) === over.id);

				if (activeIndex === -1 || overIndex === -1) {
					return;
				}

				movedItem = list[activeIndex];
				index = overIndex;
				nextColumns =
					activeIndex === overIndex
						? columns
						: {
								...columns,
								[currentContainer]: arrayMove(list, activeIndex, overIndex),
							};
			} else {
				const lifted = liftItem(columns, columnIds, getItemId, itemId);
				movedItem = lifted.item;

				if (!movedItem) {
					return;
				}

				const target = lifted.columns[overContainer] ?? [];
				const overIndex = isColumn(over.id)
					? -1
					: target.findIndex((item: T) => getItemId(item) === over.id);

				index = overIndex === -1 ? target.length : overIndex;
				target.splice(index, 0, movedItem);
				lifted.columns[overContainer] = target;
				nextColumns = lifted.columns;
			}

			// Re-check the release target: an earlier hover may have previewed the card
			// into a legal column before the pointer settled over a refused one.
			if (
				canDrop &&
				from !== overContainer &&
				!canDrop({ item: movedItem, from, to: overContainer, overIndex: index })
			) {
				restore();
				onDropRejected?.({ item: movedItem, from, to: overContainer });
				return;
			}

			const didChangeColumn = from !== overContainer;

			// Every branch above returns a fresh object when it changes something, so
			// an unchanged reference means nothing moved. A cross-column drop still
			// has to be reported: `handleDragOver` already previewed the card into the
			// target, so landing on that previewed slot leaves the reference untouched.
			if (nextColumns === columns && !didChangeColumn) {
				return;
			}

			if (nextColumns !== columns) {
				setColumns(nextColumns);
			}

			onMove?.({
				event,
				item: movedItem,
				from,
				to: overContainer,
				index,
				didChangeColumn,
				restore,
			});
		},
		[
			canDrop,
			columnIds,
			columns,
			findContainer,
			getItemId,
			isColumn,
			onDragEnd,
			onDropRejected,
			onMove,
			setColumns,
		],
	);

	const contextValue = useMemo(
		() => ({
			columns,
			setColumns,
			getItemId,
			columnIds,
			activeId,
			setActiveId,
			findContainer,
			isColumn,
			modifiers,
		}),
		[
			columns,
			setColumns,
			getItemId,
			columnIds,
			activeId,
			findContainer,
			isColumn,
			modifiers,
		],
	);

	// One context serves every item type, and getItemId takes T, so T is widened.
	const sharedContextValue = contextValue as KanbanContextProps<unknown>;

	const defaultProps = {
		"data-slot": "kanban",
		"data-dragging": activeId !== null,
		className: cn(
			"group/kanban",
			activeId !== null && "cursor-grabbing!",
			className,
		),
		children,
	};

	return (
		<KanbanContext.Provider value={sharedContextValue}>
			<DndContext
				sensors={sensors}
				modifiers={modifiers}
				accessibility={accessibility}
				collisionDetection={collisionDetection}
				measuring={MEASURING_CONFIG}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				{useRender({
					defaultTagName: "div",
					render,
					props: mergeProps<"div">(defaultProps, props),
				})}
			</DndContext>
		</KanbanContext.Provider>
	);
}

/** Set by `KanbanContainer` so `KanbanBoard` stops wrapping and overflows horizontally instead. */
const KanbanContainerContext = createContext(false);

export type KanbanContainerProps = useRender.ComponentProps<"div">;

/**
 * Scrollable viewport for a board whose columns are wider than the page.
 * Wrap `KanbanBoard` in this when you want side-scrolling instead of wrapping.
 */
function KanbanContainer({
	className,
	render,
	...props
}: KanbanContainerProps) {
	const defaultProps = {
		"data-slot": "kanban-container",
		// Focusable so keyboard users can scroll the region (WCAG 2.1.1).
		tabIndex: 0,
		role: "region",
		"aria-label": "Kanban board",
		className: cn(
			"w-full max-w-full overflow-x-auto overscroll-x-contain",
			"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
			className,
		),
		children: props.children,
	};

	return (
		<KanbanContainerContext.Provider value={true}>
			{useRender({
				defaultTagName: "div",
				render,
				props: mergeProps<"div">(defaultProps, props),
			})}
		</KanbanContainerContext.Provider>
	);
}

export type KanbanBoardProps = useRender.ComponentProps<"div">;

/** The horizontal container for kanban columns. */
function KanbanBoard({ className, render, ...props }: KanbanBoardProps) {
	const { columnIds } = useContext(KanbanContext);
	const isInsideContainer = useContext(KanbanContainerContext);

	const defaultProps = {
		"data-slot": "kanban-board",
		className: cn(
			// Stretch so every column matches the tallest one - a column is its own
			// drop target, and `items-start` would leave empty space beside neighbours
			// undroppable.
			"flex flex-row items-stretch gap-4",
			// Inside a `KanbanContainer`, grow past the viewport so overflow scrolls;
			// otherwise wrap onto extra rows.
			isInsideContainer ? "w-max flex-nowrap" : "flex-wrap",
			className,
		),
		children: props.children,
	};

	return (
		<SortableContext items={columnIds} strategy={rectSortingStrategy}>
			{useRender({
				defaultTagName: "div",
				render,
				props: mergeProps<"div">(defaultProps, props),
			})}
		</SortableContext>
	);
}

export interface KanbanColumnProps extends useRender.ComponentProps<"div"> {
	value: string;
	disabled?: boolean;
	/**
	 * Column width. Defaults to `20rem` so flex board layout works without an explicit width.
	 * Pass a CSS width (e.g. `320` or `"24rem"`) to override.
	 */
	width?: CSSProperties["width"];
	/**
	 * When true, {@link KanbanColumnContent} shows placeholder cards and
	 * {@link KanbanColumnCount} shows a skeleton badge.
	 */
	isLoading?: boolean;
	/** Number of placeholder cards when `isLoading`. Defaults to `3`. */
	skeletonCount?: number;
}

const DEFAULT_COLUMN_WIDTH = "20rem";
const DEFAULT_SKELETON_COUNT = 3;

/** An individual column within the kanban board. */
function KanbanColumn({
	value,
	className,
	render,
	disabled,
	width = DEFAULT_COLUMN_WIDTH,
	isLoading = false,
	skeletonCount = DEFAULT_SKELETON_COUNT,
	...props
}: KanbanColumnProps) {
	const isOverlay = useContext(IsOverlayContext);

	const {
		setNodeRef,
		transform,
		transition,
		attributes,
		listeners,
		isDragging: isSortableDragging,
	} = useSortable({
		id: value,
		disabled: disabled || isLoading || isOverlay,
		animateLayoutChanges,
	});

	// Hooks must run unconditionally; the derived value below is used only in the non-overlay branch.
	const { activeId, isColumn } = useContext(KanbanContext);
	const isColumnDragging = activeId ? isColumn(activeId) : false;

	const widthStyle = { width, minWidth: width } satisfies CSSProperties;

	const style = {
		transition,
		transform: CSS.Transform.toString(transform),
		...widthStyle,
	} as CSSProperties;

	const defaultProps = isOverlay
		? {
				"data-slot": "kanban-column",
				"data-value": value,
				"data-dragging": true,
				"data-loading": isLoading || undefined,
				style: widthStyle,
				className: cn(
					"group/kanban-column flex flex-col gap-3 rounded-xl bg-neutral-50 p-3",
					className,
				),
				children: props.children,
			}
		: {
				"data-slot": "kanban-column",
				"data-value": value,
				"data-dragging": isSortableDragging,
				"data-disabled": disabled,
				"data-loading": isLoading || undefined,
				"aria-busy": isLoading || undefined,
				ref: setNodeRef,
				style,
				className: cn(
					"group/kanban-column flex flex-col gap-3 rounded-xl bg-neutral-50 p-3",
					isSortableDragging && "opacity-50 z-50",
					disabled && "opacity-50",
					className,
				),
				children: props.children,
			};

	const columnContextValue = useMemo(
		() =>
			isOverlay
				? {
						id: value,
						attributes: {} as DraggableAttributes,
						listeners: undefined,
						isDragging: true,
						disabled: false,
						isLoading,
						skeletonCount,
					}
				: {
						id: value,
						attributes,
						listeners,
						isDragging: isColumnDragging,
						disabled,
						isLoading,
						skeletonCount,
					},
		[
			isOverlay,
			value,
			attributes,
			listeners,
			isColumnDragging,
			disabled,
			isLoading,
			skeletonCount,
		],
	);

	return (
		<ColumnContext.Provider value={columnContextValue}>
			{useRender({
				defaultTagName: "div",
				render,
				props: mergeProps<"div">(defaultProps, props),
			})}
		</ColumnContext.Provider>
	);
}

export interface KanbanColumnHandleProps
	extends useRender.ComponentProps<"div"> {
	cursor?: boolean;
}

/** The drag handle for a column (if columns are sortable). */
function KanbanColumnHandle({
	className,
	render,
	cursor = true,
	...props
}: KanbanColumnHandleProps) {
	const { attributes, listeners, isDragging, disabled } =
		useColumnContext("KanbanColumnHandle");

	const defaultProps = {
		"data-slot": "kanban-column-handle",
		"data-dragging": isDragging,
		"data-disabled": disabled,
		...attributes,
		...listeners,
		className: cn(
			"opacity-0 transition-opacity group-hover/kanban-column:opacity-100",
			cursor && (isDragging ? "cursor-grabbing!" : "cursor-grab!"),
			className,
		),
		children: props.children,
	};

	return useRender({
		defaultTagName: "div",
		render,
		props: mergeProps<"div">(defaultProps, props),
	});
}

export type KanbanColumnHeaderProps = useRender.ComponentProps<"div">;

function KanbanColumnHeader({
	className,
	render,
	...props
}: KanbanColumnHeaderProps) {
	const defaultProps = {
		"data-slot": "kanban-column-header",
		className: cn("flex items-center justify-between gap-2", className),
		children: props.children,
	};

	return useRender({
		defaultTagName: "div",
		render,
		props: mergeProps<"div">(defaultProps, props),
	});
}

export type KanbanColumnTitleProps = useRender.ComponentProps<"span">;

function KanbanColumnTitle({
	className,
	render,
	...props
}: KanbanColumnTitleProps) {
	const defaultProps = {
		"data-slot": "kanban-column-title",
		className: cn(
			"text-xs font-semibold uppercase tracking-wide text-neutral-500",
			className,
		),
		children: props.children,
	};

	return useRender({
		defaultTagName: "span",
		render,
		props: mergeProps<"span">(defaultProps, props),
	});
}

export interface KanbanColumnCountProps
	extends useRender.ComponentProps<"span"> {
	/**
	 * Total to display. Defaults to the number of items loaded into the column,
	 * so paginated boards should pass their server total instead.
	 */
	count?: number;
}

function KanbanColumnCount({ className, ...props }: KanbanColumnCountProps) {
	const { isLoading } = useColumnContext("KanbanColumnCount");

	// The badge is a separate component so `useRender` is never called
	// conditionally - swapping components keeps hook order stable.
	if (isLoading) {
		return (
			<Skeleton
				data-slot="kanban-column-count"
				className={cn("h-5 w-6 rounded-full", className)}
			/>
		);
	}

	return <KanbanColumnCountBadge className={className} {...props} />;
}

function KanbanColumnCountBadge({
	count: countProp,
	className,
	render,
	...props
}: KanbanColumnCountProps) {
	const { columns } = useContext(KanbanContext);
	const { id } = useColumnContext("KanbanColumnCount");
	const count = countProp ?? columns[id]?.length ?? 0;

	const defaultProps = {
		"data-slot": "kanban-column-count",
		className: cn(
			"inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-100 px-1.5 text-xs font-semibold text-primary-500",
			className,
		),
		children: props.children ?? count,
	};

	return useRender({
		defaultTagName: "span",
		render,
		props: mergeProps<"span">(defaultProps, props),
	});
}

export type KanbanColumnAddProps = useRender.ComponentProps<"button">;

function KanbanColumnAdd({
	className,
	render,
	...props
}: KanbanColumnAddProps) {
	const defaultProps = {
		"data-slot": "kanban-column-add",
		type: "button" as const,
		"aria-label": "Add item",
		className: cn(
			"inline-flex size-6 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
			className,
		),
		children: props.children ?? <PlusIcon className="size-4" />,
	};

	return useRender({
		defaultTagName: "button",
		render,
		props: mergeProps<"button">(defaultProps, props),
	});
}

export interface KanbanColumnLoadMoreProps
	extends useRender.ComponentProps<"button"> {
	/** When false, the control is not rendered. Defaults to false. */
	hasNextPage?: boolean;
	/** When true, disables the button and shows a spinner. */
	isLoading?: boolean;
}

/**
 * Presentational per-column load-more control. Wire `onClick` / `isLoading` from
 * the app's paginated query (`fetchMore`, `isFetchingMore`); pass
 * `hasNextPage={false}` when there is no next page.
 */
function KanbanColumnLoadMore({
	className,
	render,
	hasNextPage = false,
	isLoading = false,
	disabled,
	children,
	...props
}: KanbanColumnLoadMoreProps) {
	const defaultProps = {
		"data-slot": "kanban-column-load-more",
		type: "button" as const,
		disabled: disabled || isLoading,
		"aria-busy": isLoading || undefined,
		className: cn(
			"inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-neutral-200 bg-background px-2 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
			className,
		),
		children: children ?? (
			<>
				{isLoading ? <Loader2Icon className="size-3.5 animate-spin" /> : null}
				Load more
			</>
		),
	};

	const element = useRender({
		defaultTagName: "button",
		render,
		props: mergeProps<"button">(defaultProps, props),
	});

	if (!hasNextPage) {
		return null;
	}

	return element;
}

const kanbanItemBaseClassName =
	"relative flex min-w-0 flex-col gap-1 rounded-lg border border-neutral-200 bg-background p-2.5 shadow-xs";

export interface KanbanItemProps extends useRender.ComponentProps<"div"> {
	value: string;
	disabled?: boolean;
	/**
	 * When `true` (default), the whole item becomes the drag surface, so the card
	 * can be dragged from anywhere while still being clickable (a click without
	 * movement never starts a drag). Set to `false` to require an explicit
	 * {@link KanbanItemHandle} instead.
	 */
	asHandle?: boolean;
	/** Toggles the grab/grabbing cursor when the whole item is the drag surface. */
	cursor?: boolean;
}

/** An individual draggable item within a column. */
function KanbanItem({
	value,
	className,
	render,
	disabled,
	asHandle = true,
	cursor = true,
	...props
}: KanbanItemProps) {
	const isOverlay = useContext(IsOverlayContext);

	const {
		setNodeRef,
		transform,
		transition,
		attributes,
		listeners,
		isDragging: isSortableDragging,
	} = useSortable({
		id: value,
		disabled: disabled || isOverlay,
		animateLayoutChanges,
	});

	// Hooks must run unconditionally; the derived value below is used only in the non-overlay branch.
	const { activeId, isColumn } = useContext(KanbanContext);
	const isItemDragging = activeId ? !isColumn(activeId) : false;

	const style = {
		transition,
		transform: CSS.Transform.toString(transform),
	} as CSSProperties;

	const defaultProps = isOverlay
		? {
				"data-slot": "kanban-item",
				"data-value": value,
				"data-dragging": true,
				className: cn(kanbanItemBaseClassName, className),
				children: props.children,
			}
		: {
				"data-slot": "kanban-item",
				"data-value": value,
				"data-dragging": isSortableDragging,
				"data-disabled": disabled,
				ref: setNodeRef,
				style,
				...attributes,
				...(asHandle ? listeners : {}),
				className: cn(
					kanbanItemBaseClassName,
					asHandle &&
						!disabled &&
						cursor &&
						(isSortableDragging ? "cursor-grabbing!" : "cursor-grab!"),
					isSortableDragging && "opacity-50 z-50",
					disabled && "opacity-50",
					"group-data-[dragging=true]/kanban:pointer-events-none",
					className,
				),
				children: props.children,
			};

	const itemContextValue = useMemo(
		() =>
			isOverlay
				? { listeners: undefined, isDragging: true, disabled: false }
				: { listeners, isDragging: isItemDragging, disabled },
		[isOverlay, listeners, isItemDragging, disabled],
	);

	return (
		<ItemContext.Provider value={itemContextValue}>
			{useRender({
				defaultTagName: "div",
				render,
				props: mergeProps<"div">(defaultProps, props),
			})}
		</ItemContext.Provider>
	);
}

export interface KanbanItemHandleProps extends useRender.ComponentProps<"div"> {
	cursor?: boolean;
}

/** The drag handle for an individual item. */
function KanbanItemHandle({
	className,
	render,
	cursor = true,
	...props
}: KanbanItemHandleProps) {
	const { listeners, isDragging, disabled } = useContext(ItemContext);

	const defaultProps = {
		"data-slot": "kanban-item-handle",
		"data-dragging": isDragging,
		"data-disabled": disabled,
		...listeners,
		className: cn(
			cursor && (isDragging ? "cursor-grabbing!" : "cursor-grab!"),
			className,
		),
		children: props.children,
	};

	return useRender({
		defaultTagName: "div",
		render,
		props: mergeProps<"div">(defaultProps, props),
	});
}

export interface KanbanColumnContentProps
	extends useRender.ComponentProps<"div"> {
	/**
	 * Hold the loaded height while the column shows skeletons, so a refetch
	 * (sort, filter) cannot shrink the page and make the browser clamp the
	 * scroll position. Defaults to true.
	 */
	preserveLoadingHeight?: boolean;
}

/** The scrollable area within a column that holds the items. */
function KanbanColumnContent({
	className,
	render,
	children: childrenProp,
	preserveLoadingHeight = true,
	...props
}: KanbanColumnContentProps) {
	const { columns, getItemId } = useContext(KanbanContext);
	const {
		id,
		isLoading,
		skeletonCount = DEFAULT_SKELETON_COUNT,
	} = useColumnContext("KanbanColumnContent");

	const contentRef = useRef<HTMLDivElement | null>(null);
	const loadedHeightRef = useRef<number | null>(null);

	// Written straight to the node rather than through state: the floor has to be
	// in place in the same commit that swaps in the skeletons, before paint.
	useLayoutEffect(() => {
		const node = contentRef.current;
		if (!node || !preserveLoadingHeight) {
			return;
		}

		if (isLoading) {
			if (loadedHeightRef.current !== null) {
				node.style.minHeight = `${loadedHeightRef.current}px`;
			}
			return;
		}

		node.style.minHeight = "";
		loadedHeightRef.current = node.offsetHeight;

		// Keep the measurement current as cards are dragged in or pages appended,
		// without reading layout on every render.
		const observer = new ResizeObserver(([entry]) => {
			const height = entry?.contentRect.height;
			if (height) {
				loadedHeightRef.current = height;
			}
		});
		observer.observe(node);

		return () => observer.disconnect();
	}, [isLoading, preserveLoadingHeight]);

	const itemIds = useMemo(() => {
		if (isLoading) {
			return [];
		}
		const items = columns[id];
		if (!items) {
			throw new Error(
				`KanbanColumnContent: column "${id}" was not found in the Kanban value. ` +
					`Available columns: ${Object.keys(columns).join(", ") || "(none)"}.`,
			);
		}
		return items.map(getItemId);
	}, [columns, getItemId, id, isLoading]);

	const children = isLoading
		? Array.from({ length: skeletonCount }, (_, index) => (
				<div
					key={index}
					aria-hidden
					data-slot="kanban-item-skeleton"
					className={cn(kanbanItemBaseClassName, "gap-2")}
				>
					<Skeleton className="h-3 w-14" />
					<Skeleton className="h-4 w-3/4" />
					<Skeleton className="h-3 w-1/2" />
				</div>
			))
		: childrenProp;

	const defaultProps = {
		"data-slot": "kanban-column-content",
		className: cn("flex flex-col gap-2", className),
		children,
	};

	return (
		<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
			{useRender({
				defaultTagName: "div",
				render,
				// `useRender`'s own slot rather than `defaultProps`, so the ref object
				// is never handed to `mergeProps`. It merges with any caller ref.
				ref: contentRef,
				props: mergeProps<"div">(defaultProps, props),
			})}
		</SortableContext>
	);
}

export interface KanbanOverlayRenderParams<T = unknown> {
	variant: "column" | "item";
	/** Active card when dragging an item; `undefined` when dragging a column. */
	item: T | undefined;
}

export interface KanbanOverlayProps<T = unknown>
	extends Omit<React.ComponentProps<typeof DragOverlay>, "children"> {
	children?: ReactNode | ((params: KanbanOverlayRenderParams<T>) => ReactNode);
}

/** The ghost element displayed during a drag operation. */
function KanbanOverlay<T = unknown>({
	children,
	className,
	...props
}: KanbanOverlayProps<T>) {
	const { activeId, isColumn, modifiers, columns, getItemId, findContainer } =
		useContext(KanbanContext);
	const mounted = useSyncExternalStore(
		subscribeToNothing,
		getIsMounted,
		getIsMountedOnServer,
	);

	const variant = activeId ? (isColumn(activeId) ? "column" : "item") : "item";

	const item = (() => {
		if (!activeId || variant !== "item") {
			return undefined;
		}
		const container = findContainer(activeId);
		if (!container) {
			return undefined;
		}
		return columns[container]?.find(
			(candidate) => getItemId(candidate) === activeId,
		) as T | undefined;
	})();

	const content =
		activeId && children
			? typeof children === "function"
				? children({ variant, item })
				: children
			: null;

	if (!mounted) {
		return null;
	}

	return createPortal(
		<DragOverlay
			dropAnimation={dropAnimationConfig}
			modifiers={modifiers}
			className={cn("z-50", activeId && "cursor-grabbing", className)}
			{...props}
		>
			<IsOverlayContext.Provider value={true}>
				{content}
			</IsOverlayContext.Provider>
		</DragOverlay>,
		document.body,
	);
}

export type KanbanCardIdProps = useRender.ComponentProps<"div">;

/** Top line of a card, e.g. a reference like `EOI-008`. */
function KanbanCardId({ className, render, ...props }: KanbanCardIdProps) {
	const defaultProps = {
		"data-slot": "kanban-card-id",
		className: cn("text-xs font-medium text-neutral-400", className),
		children: props.children,
	};

	return useRender({
		defaultTagName: "div",
		render,
		props: mergeProps<"div">(defaultProps, props),
	});
}

export type KanbanCardBodyProps = useRender.ComponentProps<"div">;

/** Main content region of a card; lay it out however each board needs. */
function KanbanCardBody({ className, render, ...props }: KanbanCardBodyProps) {
	const defaultProps = {
		"data-slot": "kanban-card-body",
		className: cn("flex min-w-0 flex-col gap-1", className),
		children: props.children,
	};

	return useRender({
		defaultTagName: "div",
		render,
		props: mergeProps<"div">(defaultProps, props),
	});
}

export type KanbanCardFooterProps = useRender.ComponentProps<"div">;

/** Footer region of a card, e.g. a date on the left and an avatar on the right. */
function KanbanCardFooter({
	className,
	render,
	...props
}: KanbanCardFooterProps) {
	const defaultProps = {
		"data-slot": "kanban-card-footer",
		className: cn("flex items-center justify-between gap-2", className),
		children: props.children,
	};

	return useRender({
		defaultTagName: "div",
		render,
		props: mergeProps<"div">(defaultProps, props),
	});
}

export {
	Kanban,
	KanbanContainer,
	KanbanBoard,
	KanbanColumn,
	KanbanColumnHandle,
	KanbanColumnHeader,
	KanbanColumnTitle,
	KanbanColumnCount,
	KanbanColumnAdd,
	KanbanColumnLoadMore,
	KanbanItem,
	KanbanItemHandle,
	KanbanColumnContent,
	KanbanOverlay,
	KanbanCardId,
	KanbanCardBody,
	KanbanCardFooter,
};
